import React, { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Text } from '../components/Text';
import SignInPageWall from '../assets/images/SignInPageWall.png';
import Google from '../assets/images/google.png';
import { Button } from '../components/Button';
import { withNotification } from 'components/Notification';
import { NotificationTypes } from 'utils';
import { SizeEnum, validateEmail } from '../utils';
import FormInput from '../components/FormInput';
import {
  useSignInEmailPassword,
  useSignInEmailPasswordless,
  useNhostClient,
} from '@nhost/react';
import classNames from 'classnames';
import AuthPage from '../components/AuthPage';
import { getGorgiasInstallReturnPath, GORGIAS_INSTALL_RETURN_KEY } from 'utils/gorgiasInstallation';

export const Login: React.FC = withNotification(({ showNotification }: any) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaOtp, setMfaOtp] = useState('');
  const [loginWithLink, setLoginWithLink] = useState(true);
  const [errorObj, setErrorObj] = useState({
    message: '',
    error: '',
    status: 0,
  });
  const navigate = useNavigate();
  const shopifySignupURI = localStorage.getItem('shipaid___post-install');
  const gorgiasInstallReturnPath = getGorgiasInstallReturnPath();

  const nhost = useNhostClient();

  const { signInEmailPasswordless } = useSignInEmailPasswordless({
    redirectTo: gorgiasInstallReturnPath || '/login',
  });

  const {
    signInEmailPassword,
    isLoading,
    isSuccess,
    needsEmailVerification,
    needsMfaOtp,
    sendMfaOtp,
    error,
  } = useSignInEmailPassword();

  useEffect(() => {
    if (error) {
      setErrorObj({ ...error });
    }
  }, [error]);

  const renderForgotPasswordLink = () => {
    return (
      <span className="text-sm block text-shipaidBlue">
        <Link to="/forgot-password">Forgot your password?</Link>
      </span>
    );
  };

  const toggleLoginFields = () => {
    setLoginWithLink(!loginWithLink);
  };

  function handleValidation() {
    if (email.trim() === '') {
      const error = { message: 'Email is required', error: 'invalid-email' };
      setErrorObj({ ...errorObj, ...error });
      return error;
    }
    if (!validateEmail(email)) {
      const error = {
        message: 'Email is not valid, please enter a valid email',
        error: 'invalid-email',
      };
      setErrorObj({ ...errorObj, ...error });
      return error;
    }
    if (password.trim() === '') {
      const error = {
        message: 'Password is required',
        error: 'invalid-password',
      };
      setErrorObj({ ...errorObj, ...error });
      return error;
    }
    setErrorObj({ message: '', error: '', status: 0 });
    return false;
  }

  const handleLogin = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const hasError = handleValidation();
    if (!hasError) signInEmailPassword(email, password);
  };

  // Reached when the account has two-factor authentication enabled (see Account.tsx) — the
  // email/password step above already succeeded and put the sign-in flow into a "needsMfaOtp"
  // state; this is the second-factor step before isSuccess/navigate below actually fires.
  const handleMfaSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    sendMfaOtp?.(mfaOtp);
  };

  const handleMagicLinkLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const result = await signInEmailPasswordless(email);
    if (result.isError) {
      showNotification(NotificationTypes.error, result.error?.message || null);
    } else {
      showNotification(
        NotificationTypes.success,
        'A Login link has been sent to your mail',
      );
    }
  };

  // Set by KlaviyoMarketplaceInstall.tsx when a merchant arrives from Klaviyo's marketplace
  // without an active session — same "remember where to resume after login" pattern as
  // shopifySignupURI above, just for the Klaviyo-install entry point instead of the Shopify one.
  const klaviyoInstallReturnPath = localStorage.getItem(
    'shipaid___klaviyo-install-pending',
  );

  if (isSuccess && gorgiasInstallReturnPath) {
    localStorage.removeItem(GORGIAS_INSTALL_RETURN_KEY);
    navigate(gorgiasInstallReturnPath);
  } else if (isSuccess && shopifySignupURI) {
    window.location.href = shopifySignupURI;
  } else if (isSuccess && klaviyoInstallReturnPath) {
    localStorage.removeItem('shipaid___klaviyo-install-pending');
    navigate(klaviyoInstallReturnPath);
  } else if (isSuccess) {
    navigate('/');
  }

  function handleSignInWithGoogle() {
    nhost.auth.signIn({
      provider: 'google',
      options: {
        redirectTo: gorgiasInstallReturnPath || shopifySignupURI || klaviyoInstallReturnPath || '/',
      },
    });
  }

  const disableForm = isLoading || needsEmailVerification;

  if (needsMfaOtp) {
    return (
      <AuthPage backgroundImage={SignInPageWall}>
        <div className="text-center my-6">
          <h6 className="uppercase text-shipaidBlue my-4 font-bold">Account</h6>
          <h2 className="text-3xl md:text-5xl my-4">Enter your code</h2>
          <Text
            type="body"
            value="Enter the 6-digit code from your authenticator app"
            className="text-gray-500"
          />
        </div>
        <div className="form w-full md:w-[450px]">
          <form className="w-full" onSubmit={handleMfaSubmit}>
            <div className="mb-4">
              <FormInput
                id="mfaOtp"
                name="mfaOtp"
                label="Authentication code"
                value={mfaOtp}
                onChange={(e) => setMfaOtp(e.target.value)}
                placeholder="6-digit code"
                type="text"
              />
            </div>
            <Text
              type="sm"
              value={errorObj?.message}
              className={classNames('text-center text-red-600')}
            />
            <Button
              size={SizeEnum.xl}
              isRounded
              type="submit"
              disabled={isLoading || mfaOtp.length !== 6}
              className="text-white w-full rounded-full flex justify-center items-center align-middle text-center mt-4 hover:bg-opacity-80"
            >
              Verify
            </Button>
          </form>
        </div>
      </AuthPage>
    );
  }

  return (
    <AuthPage backgroundImage={SignInPageWall}>
      <div className="text-center my-6">
        <h6 className="uppercase text-shipaidBlue my-4 font-bold">Account</h6>

        <h2 className="text-3xl md:text-5xl my-4">Log in to continue</h2>

        <div className="text-sm md:text-xl">
          <span>Don&apos;t have an account? </span>
          <span className="text-shipaidBlue">
            <Link to="/sign-up">Sign up</Link>
          </span>
        </div>

        <Button
          size={SizeEnum.sm}
          isRounded
          onClick={handleSignInWithGoogle}
          className="bg-transparent border border-black w-full rounded-full flex justify-center items-center align-middle text-center mt-4"
        >
          <img className="h-6 w-6" src={Google} alt="google logo" />
          <span className="block pl-2"> Login with google</span>
        </Button>

        <div className="my-4">
          <fieldset className="border-t border-slate-300">
            <legend className="mx-auto px-2 text-black text-sm">
              Or Continue with
            </legend>
          </fieldset>
        </div>
      </div>
      <div className="form w-full  md:w-[450px]">
        <form
          className="w-full"
          onSubmit={loginWithLink ? handleMagicLinkLogin : handleLogin}
        >
          <div className="mb-4">
            <FormInput
              id="email"
              name="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              type="email"
            />
          </div>

          {loginWithLink === false ? (
            <>
              <div className="mb-4">
                <FormInput
                  id="password"
                  name="password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  type="password"
                  otherInfo={renderForgotPasswordLink()}
                />
              </div>
              <Text
                type="sm"
                value={errorObj?.message}
                className={classNames('text-center text-red-600')}
              />
            </>
          ) : null}
          <Button
            size={SizeEnum.xl}
            isRounded
            type="submit"
            disabled={disableForm}
            className="text-white w-full rounded-full flex justify-center items-center align-middle text-center mt-4 hover:bg-opacity-80"
          >
            {loginWithLink === false ? 'Login' : 'Send Link'}
          </Button>
          <Button
            size={SizeEnum.sm}
            onClick={toggleLoginFields}
            className="!bg-transparent w-full rounded-full flex justify-center items-center align-middle text-black text-center mt-4"
          >
            <span className="block pl-2">
              {loginWithLink === false
                ? 'Login with magic link'
                : 'Login with Password'}
            </span>
          </Button>
        </form>
      </div>
    </AuthPage>
  );
});
